import React, { useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import {
  GetGuestFacilityScorecard,
  IGuestCourseScorecard,
} from "api/rpc/2024-04/guest/facility";
import { IGuestSponsorship } from "api/rpc/2024-04/guest/league";
import { TWeekDays } from "components/form/calendarField/helpers";
import { IFacility } from "pages/guest/models/facility";
import Page from "components/page/Page";
import Card from "components/card/Card";
import { ButtonNew as Button } from "components/buttonNew";
import Markdown from "components/markdown/Markdown";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import { Select } from "components/select/";
import { buildCalendarFile, downloadCalendarFile } from "./calendarFile";
import "./EventPublicHome.scss";

export type RegistrationAvailability = "closed" | "will-open" | "closed-previously" | "no-capacity" | "none";

interface IEventPublicHomeProps {
  name: string;
  description?: string;
  facility: IFacility;
  participantCount: number;
  registerPath: string;
  sponsors: IGuestSponsorship[];
  calendarUid: string;
  startDate: string;
  endDate?: string;
  startTime?: string;
  weekDay?: TWeekDays;
  registrationAvailability: RegistrationAvailability;
  registrationOpenDate?: string;
  registrationCloseDate?: string;
}

function sumNumbers(values: Array<number | null | undefined>) {
  return values.reduce((total, value) => total + (Number(value) || 0), 0);
}

function registrationUnavailableReason(
  availability: RegistrationAvailability,
  openDate: string | undefined,
  closeDate: string | undefined,
  t: (key: string) => string,
) {
  switch (availability) {
    case "will-open":
      return openDate
        ? `${t("Registration opens on")} ${formatRegistrationDate(openDate)}`
        : t("Registration is not open yet");
    case "closed-previously":
      return closeDate
        ? `${t("Registration ended on")} ${formatRegistrationDate(closeDate)}`
        : t("Registration is closed");
    case "no-capacity":
      return t("Registration is full");
    case "closed":
      return t("Registration is not open");
    default:
      return "";
  }
}

function formatRegistrationDate(date?: string) {
  if (!date) {
    return "";
  }

  return new Date(date + "Z").toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatAddress(facility: IFacility) {
  return [facility.address_line_1, facility.address_line_2, facility.city, facility.province_name, facility.postal]
    .filter(Boolean)
    .join(", ");
}

export default function EventPublicHome(props: IEventPublicHomeProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const [scorecardsOpen, setScorecardsOpen] = useState(false);
  const [scorecards, setScorecards] = useState<IGuestCourseScorecard[] | undefined>(undefined);
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(undefined);

  const address = formatAddress(props.facility);
  const selectedCourse = scorecards?.find(course => course.id === selectedCourseId) ?? scorecards?.[0];
  const registrationReason = registrationUnavailableReason(
    props.registrationAvailability,
    props.registrationOpenDate,
    props.registrationCloseDate,
    t,
  );

  async function openScorecards() {
    setScorecardsOpen(true);
    if (scorecards) {
      return;
    }

    const source = axios.CancelToken.source();
    const response = await GetGuestFacilityScorecard({ facility_id: props.facility.id }, false, source.token);
    if (response.status !== StatusCode.OK) {
      setScorecards([]);
      return;
    }

    setScorecards(response.data ?? []);
    setSelectedCourseId(response.data?.[0]?.id);
  }

  function addToCalendar() {
    const contents = buildCalendarFile({
      uid: props.calendarUid,
      summary: props.name,
      location: address || undefined,
      timeZone: props.facility.timezone,
      startDate: props.startDate,
      endDate: props.endDate,
      startTime: props.startTime,
      weekDay: props.weekDay,
    });
    const filename = `${props.name.replace(/[^\w]+/g, "-").replace(/^-|-$/g, "") || "event"}.ics`;
    downloadCalendarFile(filename, contents);
  }

  return (
    <>
      <Page className="event-public-home" splitLayout>
        <Page.Section twoThirds>
          <Card>
            <Card.Section>
              {props.description ? (
                <div className="event-public-home-description">
                  <Markdown markdownText={props.description} />
                </div>
              ) : (
                <p>{t("No description has been added.")}</p>
              )}
            </Card.Section>
          </Card>
        </Page.Section>
        <Page.Section oneThird>
          <Card className="event-public-home-details" title={t("Details")}>
            <Card.Section>
              <div className="event-public-home-list">
                <button type="button" className="event-public-home-row" onClick={addToCalendar}>
                  <FontAwesomeIcon fixedWidth icon={["far", "calendar-plus"]} />
                  <span>{t("Add to your calendar")}</span>
                </button>
                <div className="event-public-home-row">
                  <FontAwesomeIcon fixedWidth icon={["far", "users"]} />
                  <span>
                    {props.participantCount ?? 0}{" "}
                    {(props.participantCount ?? 0) === 1 ? t("player registered") : t("players registered")}
                  </span>
                </div>
                {/* {address && (
                  <a className="event-public-home-row" href={mapsUrl} target="_blank" rel="noreferrer">
                    <FontAwesomeIcon fixedWidth icon={["far", "location-dot"]} />
                    <span>{address}</span>
                  </a>
                )} */}
                <button type="button" className="event-public-home-row" onClick={() => void openScorecards()}>
                  <FontAwesomeIcon fixedWidth icon={["far", "clipboard"]} />
                  <span>{t("View scorecards")}</span>
                </button>
              </div>
            </Card.Section>
            <Card.Section>
              <Button
                block
                type="primary"
                disabled={Boolean(registrationReason)}
                onClick={() => history.push(props.registerPath)}
              >
                {t("Register")}
                <FontAwesomeIcon className="ml-2" icon={"arrow-right"} />
              </Button>
              {registrationReason && <p className="event-public-home-register-reason">{registrationReason}</p>}
            </Card.Section>
          </Card>
          {props.sponsors.length > 0 && (
            <Card title={t("Sponsors")}>
              <Card.Section>
                <div className="event-public-home-sponsors">
                  {props.sponsors.map(sponsor => (
                    <img key={sponsor.id} src={sponsor.source} alt={t("Sponsor")} />
                  ))}
                </div>
              </Card.Section>
            </Card>
          )}
        </Page.Section>
      </Page>
      <Portal isMounted={scorecardsOpen}>
        <Popup
          open={scorecardsOpen}
          title={t("Scorecards")}
          hideFooter
          noFixedWidth
          onCancel={() => setScorecardsOpen(false)}
          backDropCancel
        >
          {scorecards === undefined && <p>{t("Loading scorecards...")}</p>}
          {scorecards?.length === 0 && <p>{t("A course scorecard is not available for this facility.")}</p>}
          {scorecards && scorecards.length > 1 && (
            <div className="event-public-home-scorecard-courses">
              <Select
                defaultValue={selectedCourse?.id}
                onChange={(value: number) => setSelectedCourseId(value)}
              >
                {scorecards.map(course => (
                  <Select.Option key={course.id} value={course.id}>
                    {course.long_name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}
          {selectedCourse?.divisions.map(division => {
            const holeCount = division.par?.length || division.holes || 0;
            const holes = Array.from({ length: holeCount }, (_, index) => index);
            return (
              <div key={division.id} className="event-public-home-scorecard-division">
                <div className="event-public-home-scorecard-title">{division.title || selectedCourse.long_name}</div>
                <table className="event-public-home-scorecard-table">
                  <thead>
                    <tr>
                      <th>{t("Hole")}</th>
                      {holes.map(index => (
                        <th key={index}>{index + 1}</th>
                      ))}
                      <th>{t("Total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t("Par")}</td>
                      {holes.map(index => (
                        <td key={index}>{division.par?.[index] ?? ""}</td>
                      ))}
                      <td>{sumNumbers(division.par)}</td>
                    </tr>
                    <tr>
                      <td>{t("Handicap")}</td>
                      {holes.map(index => (
                        <td key={index}>{division.handicap?.[index] ?? ""}</td>
                      ))}
                      <td />
                    </tr>
                    {selectedCourse.tee_decks.map(teeDeck => {
                      const yards = teeDeck.yardages.find(yardage => yardage.division_id === division.id)?.yards ?? [];
                      return (
                        <tr key={teeDeck.id}>
                          <td>{teeDeck.title}</td>
                          {holes.map(index => (
                            <td key={index}>{yards[index] ?? ""}</td>
                          ))}
                          <td>{sumNumbers(yards)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </Popup>
      </Portal>
    </>
  );
}
